import DOMPurify from 'dompurify';

import { getActiveMailerDriver } from '../drivers/getActiveDriver';

export const MAILBOX_PAGE_SIZE = 20;
export const MAILBOX_AUTO_SYNC_INTERVAL_MS = 60 * 1000;

export const SHOW_MAILBOX_BULK_ACTIONS = true;
export const SHOW_MAILBOX_ROW_CHECKBOXES = true;

export const MAILBOX_FOLDER_API = {
    inbox: 'INBOX',
    sent: 'SENT',
    trash: 'TRASH',
    starred: 'STARRED',
};

export const normalizeMailboxUnreadCount = (payload) => {
    const root = payload?.data !== undefined && !Array.isArray(payload.data)
        ? payload.data
        : payload;
    const count = Number(root?.unread_count ?? root?.unreadCount ?? 0);

    if (!Number.isFinite(count) || count <= 0) {
        return 0;
    }

    return Math.floor(count);
};

export const getMailboxFolderItems = () => {
    const { routes } = getActiveMailerDriver();

    return [
        {
            key: 'inbox',
            titleKey: 'Inbox',
            path: routes.inbox,
            icon: 'mdi mdi-email-outline',
        },
        {
            key: 'starred',
            titleKey: 'Starred',
            path: routes.starred,
            icon: 'mdi mdi-star-outline',
        },
        {
            key: 'sent',
            titleKey: 'Sent Mail',
            path: routes.sent,
            icon: 'mdi mdi-email-check-outline',
        },
        {
            key: 'trash',
            titleKey: 'Trash',
            path: routes.trash,
            icon: 'mdi mdi-trash-can-outline',
        },
    ];
};

export const MAILBOX_FLAG_FILTERS = [
    {
        key: 'unread',
        titleKey: 'Unread',
        icon: 'mdi mdi-email-outline',
    },
    {
        key: 'starred',
        titleKey: 'Starred',
        icon: 'mdi mdi-star-outline',
    },
];

export const getMailboxComposePath = () => getActiveMailerDriver().routes.compose;
export const getMailboxSentPath = () => getActiveMailerDriver().routes.sent;
export const getMailboxInboxPath = () => getActiveMailerDriver().routes.inbox;

export const getMailboxFolderItem = (folder) =>
    getMailboxFolderItems().find((item) => item.key === folder) || null;

export const getMailboxFolderPath = (folder) =>
    getMailboxFolderItem(folder)?.path || getMailboxInboxPath();

export const getMailboxFolderItemByPath = (path) =>
    getMailboxFolderItems().find((item) => item.path === path) || null;

export const getMailboxFolderItemByApiFolder = (folder) => {
    const value = String(folder || '').trim();

    if (!value) {
        return null;
    }

    const byKey = getMailboxFolderItem(value.toLowerCase());

    if (byKey) {
        return byKey;
    }

    const apiKey = Object.keys(MAILBOX_FOLDER_API).find(
        (key) => MAILBOX_FOLDER_API[key] === value.toUpperCase()
    );

    return getMailboxFolderItem(apiKey);
};

export const getMailboxIntentItems = () => getActiveMailerDriver().intents.items || [];

export const mailboxHasIntents = () => getMailboxIntentItems().length > 0;

export const getMailboxSidebarLabelCodes = () => (
    getActiveMailerDriver().intents.sidebarLabelCodes || []
);

export const MAILBOX_INTENT_DATA_SUBJECT_REQUEST = 'data_subject_request';
export const MAILBOX_INTENT_DATA_BREACH_INCIDENT = 'data_breach_incident';

export const parseMailboxAddressList = (value) =>
    String(value || '')
        .split(/[,;\n]+/)
        .map((item) => item.trim())
        .filter(Boolean);

export const isValidMailboxEmail = (value) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());

export const escapeMailboxHtml = (value) => String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

export const buildComposeBodies = (value, original = null) => {
    const replyText = String(value || '');
    const escaped = escapeMailboxHtml(replyText).replace(/\n/g, '<br />');
    const replyHtml = replyText.trim() ? `<p>${escaped}</p>` : '';

    if (!original) {
        return {
            bodyText: replyText,
            bodyHtml: replyHtml,
        };
    }

    const originalText = buildOriginalMessagePlainText(original);
    const originalHtml = buildOriginalMessageHtml(original);

    return {
        bodyText: [replyText.trim(), originalText].filter(Boolean).join('\n\n'),
        bodyHtml: [replyHtml, originalHtml].filter(Boolean).join(''),
    };
};

const collectMailboxEmails = (value) => {
    if (!value) {
        return [];
    }

    if (Array.isArray(value)) {
        return value.flatMap(collectMailboxEmails).filter(Boolean);
    }

    if (typeof value === 'string') {
        return parseMailboxAddressList(value)
            .map((item) => parseMailboxParty(item).email)
            .filter((email) => isValidMailboxEmail(email));
    }

    const email = parseMailboxParty(value).email;

    return isValidMailboxEmail(email) ? [email] : [];
};

export const getMessageToEmails = (message) => {
    const source = asMessage(message);

    return collectMailboxEmails(
        source.to_addresses ||
        source.to ||
        source.recipients ||
        source.to_email ||
        source.toEmail
    );
};

const stripMailboxMarkup = (value) => String(value || '')
    .replace(/<head\b[^>]*>[\s\S]*?<\/head>/gi, ' ')
    .replace(/<style\b[^>]*>[\s\S]*?<\/style>/gi, ' ')
    .replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, ' ')
    .replace(/<noscript\b[^>]*>[\s\S]*?<\/noscript>/gi, ' ')
    .replace(/<!--[\s\S]*?-->/g, ' ')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/[ \t]{2,}/g, ' ')
    .trim();

export const stripMailboxHtml = (value) => trimMailboxQuotedText(stripMailboxMarkup(value));

const CSS_RULE_LINE = /^[ \t]*[^{}\n]+\{[^{}\n]*:[^{}\n]*\}[ \t]*$/;
const OUTLOOK_QUOTED_HEADER = new RegExp(
    '(?:^|\\n)[ \\t]*(?:-{2,}\\s*Original Message\\s*-{2,}[ \\t]*\\n+)?'
    + '[ \\t]*From:[ \\t]*\\S[^\\n]*'
    + '(?:\\n+[ \\t]*(?:Sent|Date):[^\\n]*\\d[^\\n]*)'
    + '(?:\\n+[ \\t]*To:[ \\t]*\\S[^\\n]*)'
    + '(?:\\n+[ \\t]*Cc:[ \\t]*\\S[^\\n]*)?'
    + '(?:\\n+[ \\t]*Subject:[ \\t]*[^\\n]*)',
    'i'
);

export const trimMailboxQuotedText = (value) => {
    const withoutCss = String(value || '')
        .split('\n')
        .filter((line) => !CSS_RULE_LINE.test(line))
        .join('\n');
    const quotedHeader = withoutCss.match(OUTLOOK_QUOTED_HEADER);

    if (!quotedHeader || quotedHeader.index == null) {
        return withoutCss.trim();
    }

    return withoutCss.slice(0, quotedHeader.index).trim();
};

const startsWithOutlookQuote = (value) => (
    OUTLOOK_QUOTED_HEADER.test(String(value || '').trim())
);

const elementPlainText = (element) => {
    let text = '';

    element.childNodes.forEach((child) => {
        if (child.nodeType === Node.TEXT_NODE) {
            text += child.textContent;
            return;
        }

        if (child.nodeType !== Node.ELEMENT_NODE) {
            return;
        }

        if (child.tagName === 'BR') {
            text += '\n';
            return;
        }

        text += `\n${elementPlainText(child)}`;
    });

    return text;
};

const removeFromNodeToEnd = (node) => {
    if (!node) {
        return;
    }

    const previous = node.previousSibling;

    if (previous?.nodeType === Node.ELEMENT_NODE && previous.tagName === 'HR') {
        previous.remove();
    }

    let current = node;

    while (current) {
        const next = current.nextSibling;
        current.remove();
        current = next;
    }
};

const quoteLineEntries = (element) => {
    const entries = [];
    const visit = (node) => {
        if (node.nodeType === Node.TEXT_NODE) {
            String(node.textContent).split('\n').forEach((part) => {
                entries.push({
                    text: part.trim(),
                    node,
                });
            });
            return;
        }

        if (node.nodeType !== Node.ELEMENT_NODE) {
            return;
        }

        if (node.tagName === 'BR') {
            entries.push({
                text: '',
                node,
            });
            return;
        }

        const hasBlocks = [...node.children].some((child) => (
            child.tagName === 'BR'
            || child.tagName === 'TABLE'
            || /^(?:P|DIV|LI|BLOCKQUOTE|PRE|H[1-6]|TR)$/.test(child.tagName)
        ));

        if (!hasBlocks && node.childNodes.length) {
            entries.push({
                text: elementPlainText(node).replace(/\s+/g, ' ').trim(),
                node,
            });
            return;
        }

        [...node.childNodes].forEach(visit);
    };

    [...element.childNodes].forEach(visit);

    return entries;
};

const findOutlookQuoteEntry = (entries) => {
    const lines = entries.filter((entry) => entry.text);

    for (let index = 0; index < lines.length; index += 1) {
        const from = lines[index];
        const sent = lines[index + 1];
        const to = lines[index + 2];

        if (!from || !sent || !to) {
            return null;
        }

        if (!/^from:\s*\S/i.test(from.text)) {
            continue;
        }

        if (!/^(?:sent|date):/i.test(sent.text) || !/\d/.test(sent.text)) {
            continue;
        }

        if (!/^to:\s*\S/i.test(to.text)) {
            continue;
        }

        let subjectIndex = index + 3;

        if (lines[subjectIndex] && /^cc:\s*\S/i.test(lines[subjectIndex].text)) {
            subjectIndex += 1;
        }

        if (lines[subjectIndex] && /^subject:/i.test(lines[subjectIndex].text)) {
            return from;
        }
    }

    return null;
};

const trimQuotedElement = (element) => {
    [...element.childNodes].forEach((child) => {
        const text = child.nodeType === Node.TEXT_NODE
            ? child.textContent
            : elementPlainText(child);

        if (CSS_RULE_LINE.test(String(text || '').trim())) {
            child.remove();
        }
    });

    const quote = findOutlookQuoteEntry(quoteLineEntries(element));

    if (!quote) {
        return;
    }

    if (quote.node.nodeType === Node.TEXT_NODE) {
        if (OUTLOOK_QUOTED_HEADER.test(quote.node.textContent)) {
            quote.node.textContent = trimMailboxQuotedText(quote.node.textContent);
            removeFromNodeToEnd(quote.node.nextSibling);
            return;
        }

        removeFromNodeToEnd(quote.node);
        return;
    }

    if (quote.node === element) {
        element.textContent = trimMailboxQuotedText(elementPlainText(element));
        return;
    }

    if (startsWithOutlookQuote(elementPlainText(quote.node))) {
        removeFromNodeToEnd(quote.node);
        return;
    }

    if (quote.node.nodeType === Node.ELEMENT_NODE) {
        trimQuotedElement(quote.node);
    }
};

const trimQuotedMailboxHtml = (html) => {
    if (!html || typeof DOMParser === 'undefined') {
        return html;
    }

    const document = new DOMParser().parseFromString(String(html), 'text/html');

    document.querySelectorAll(
        '.mailbox-reply-original-content, .gmail_quote, .gmail_attr'
    ).forEach((node) => node.remove());

    ['appendonsend', 'divRplyFwdMsg'].forEach((id) => {
        const marker = document.getElementById(id);

        if (marker) {
            removeFromNodeToEnd(marker);
        }
    });

    if (document.body) {
        trimQuotedElement(document.body);
    }

    return document.body?.innerHTML || '';
};

export const buildMailboxReplyDraft = (message, { folder } = {}) => {
    const from = getMessageFromParty(message);
    const folderName = String(asMessage(message).folder || asMessage(message).imap_folder || '').toUpperCase();
    const isSent = folder === 'sent' || folderName === 'SENT';
    const toEmails = isSent
        ? getMessageToEmails(message)
        : (from.email && isValidMailboxEmail(from.email) ? [from.email] : []);
    const subject = String(getMessageSubject(message) || '').trim();
    const replySubject = subject
        ? (/^re:/i.test(subject) ? subject : `Re: ${subject}`)
        : 'Re:';
    const html = getMessageBodyHtml(message);
    const text = getMessageBodyText(message) || stripMailboxHtml(html);

    return {
        to: toEmails.join(', '),
        cc: '',
        subject: replySubject,
        body: '',
        inReplyTo: getMessageId(message) ? String(getMessageId(message)) : '',
        original: {
            from: from.display || from.email || '',
            date: formatMailboxDate(getMessageDate(message)),
            to: getMessageTo(message),
            subject,
            html,
            text,
        },
    };
};

export const extractMailboxApiMessage = (errorOrData) => {
    const data = errorOrData?.response?.data || errorOrData;
    const message = data?.message;

    if (typeof message === 'string' && message) {
        return message;
    }

    if (message?.description) {
        return message.description;
    }

    if (data?.error && typeof data.error === 'string') {
        return data.error;
    }

    return '';
};

export const getMailboxViewFromPath = (pathname = '') => {
    if (pathname.includes('/email/compose')) {
        return {
            view: 'compose',
            folder: null,
            messageId: null,
            titleKey: 'Compose',
        };
    }

    if (pathname.includes('/email/messages/')) {
        const messageId = pathname.split('/email/messages/')[1] || null;

        return {
            view: 'detail',
            folder: null,
            messageId,
            titleKey: 'Message',
        };
    }

    if (pathname.endsWith('/email/sent')) {
        return {
            view: 'folder',
            folder: 'sent',
            messageId: null,
            titleKey: 'Sent Mail',
        };
    }

    if (pathname.endsWith('/email/trash')) {
        return {
            view: 'folder',
            folder: 'trash',
            messageId: null,
            titleKey: 'Trash',
        };
    }

    if (pathname.endsWith('/email/starred')) {
        return {
            view: 'folder',
            folder: 'starred',
            messageId: null,
            titleKey: 'Starred',
        };
    }

    return {
        view: 'folder',
        folder: 'inbox',
        messageId: null,
        titleKey: 'Inbox',
    };
};

export const getMailboxMessageDetailPath = (id) =>
    getActiveMailerDriver().routes.messageDetail.replace(':id', id);

const isTruthyMailboxFlag = (value) =>
    value === true ||
    value === 1 ||
    value === '1' ||
    String(value).toLowerCase() === 'true';

export const isReportFromMailbox = (report) => {
    if (!report || typeof report !== 'object') {
        return false;
    }

    return isTruthyMailboxFlag(report.from_mailbox) ||
        isTruthyMailboxFlag(report.fromMailbox) ||
        isTruthyMailboxFlag(report.mailboxOrigin);
};

export const getReportMailerMessageId = (report) => {
    const id = report?.mailer_message_id ?? report?.mailerMessageId ?? null;

    if (id === null || id === undefined || id === '' || id === 0 || id === '0') {
        return null;
    }

    return id;
};

export const getReportMailboxMessage = (report) => {
    const message = report?.mailbox_message ?? report?.mailboxMessage;

    if (!message || typeof message !== 'object' || Array.isArray(message)) {
        return null;
    }

    return message;
};

/**
 * Case details only expose mailbox_message when a gd_mailbox_message_case link
 * exists; list payloads expose from_mailbox instead.
 */
export const isReportCreatedFromEmail = (report) => (
    !!getReportMailboxMessage(report)
        || isReportFromMailbox(report)
        || getReportMailerMessageId(report) !== null
);

export const getFromMailboxListQuery = (fromMailbox) => (
    isTruthyMailboxFlag(fromMailbox) ? { fromMailbox: 1 } : {}
);

const asArray = (value) => {
    if (Array.isArray(value)) {
        return value;
    }

    if (Array.isArray(value?.data)) {
        return value.data;
    }

    return [];
};

export const normalizeMailboxListResponse = (payload) => {
    const root = payload?.data !== undefined ? payload.data : payload;

    if (Array.isArray(root)) {
        return {
            messages: root,
            total: root.length,
            page: 1,
            limit: MAILBOX_PAGE_SIZE,
        };
    }

    const messages = asArray(
        root?.messages ||
        root?.items ||
        root?.list ||
        root?.data ||
        []
    );
    const meta = root?.meta || payload?.meta || {};
    const total = Number(
        root?.total ??
        root?.totalCount ??
        meta.total ??
        messages.length
    ) || 0;
    const page = Number(root?.page ?? meta.page ?? 1) || 1;
    const limit = Number(
        root?.limit ??
        root?.per_page ??
        meta.limit ??
        MAILBOX_PAGE_SIZE
    ) || MAILBOX_PAGE_SIZE;

    return {
        messages,
        total,
        page,
        limit,
    };
};

const asMessage = (message) => message || {};

export const getMessageId = (message) => {
    const source = asMessage(message);

    return source.id ?? source.messageId ?? source.message_id ?? null;
};

export const normalizeMailboxMessage = (payload) => {
    if (!payload || typeof payload !== 'object') {
        return null;
    }

    const root = payload.data !== undefined && !Array.isArray(payload.data)
        ? payload.data
        : payload;

    if (root?.message && typeof root.message === 'object' && !Array.isArray(root.message)) {
        const { message, ...rest } = root;

        return {
            ...rest,
            ...message,
        };
    }

    if (
        root?.id ||
        root?.messageId ||
        root?.message_id ||
        root?.subject ||
        root?.body_html ||
        root?.bodyHtml ||
        root?.body_text ||
        root?.bodyText
    ) {
        return root;
    }

    return null;
};

const EMPTY_MAILBOX_PARTY = {
    name: '',
    email: '',
    display: '',
};

export const parseMailboxParty = (value) => {
    if (Array.isArray(value)) {
        return parseMailboxParty(value[0]);
    }

    if (value && typeof value === 'object') {
        if (value.emailAddress || value.email_address) {
            return parseMailboxParty(value.emailAddress || value.email_address);
        }

        const mailbox = value.mailbox || value.Mailbox || '';
        const host = value.host || value.Host || '';
        const name =
            value.name ||
            value.from_name ||
            value.displayName ||
            value.display_name ||
            value.personal ||
            value.Name ||
            '';
        const email =
            value.email ||
            value.address ||
            value.from_email ||
            value.fromEmail ||
            value.mail ||
            value.Mail ||
            value.Address ||
            value.Email ||
            (mailbox && host ? `${mailbox}@${host}` : '');

        if (!name && !email && value.full) {
            return parseMailboxParty(value.full);
        }

        return {
            name,
            email,
            display: name || email,
        };
    }

    if (typeof value === 'string' && value) {
        const match = value.match(/^(.*?)\s*<([^>]+)>\s*$/);

        if (match) {
            const name = match[1].trim().replace(/^["']|["']$/g, '');
            const email = match[2].trim();

            return {
                name,
                email,
                display: name || email,
            };
        }

        if (value.includes('@')) {
            return {
                name: '',
                email: value,
                display: value,
            };
        }

        return {
            name: value,
            email: '',
            display: value,
        };
    }

    return { ...EMPTY_MAILBOX_PARTY };
};

const firstMailboxParty = (...values) => {
    for (let index = 0; index < values.length; index += 1) {
        const parsed = parseMailboxParty(values[index]);

        if (parsed.display) {
            return parsed;
        }
    }

    return { ...EMPTY_MAILBOX_PARTY };
};

const pickPartyString = (...values) => {
    for (let index = 0; index < values.length; index += 1) {
        if (typeof values[index] === 'string' && values[index]) {
            return values[index];
        }
    }

    return '';
};

export const getMessageFromParty = (message) => {
    const source = asMessage(message);
    const headers = source.headers || source.header || {};
    const envelope = source.envelope || {};
    const parsed = firstMailboxParty(
        source.from,
        source.From,
        source.sender,
        source.Sender,
        source.from_address,
        source.fromAddress,
        source.sender_address,
        source.senderAddress,
        source.from_email,
        source.fromEmail,
        source.sender_email,
        source.senderEmail,
        headers.from,
        headers.From,
        envelope.from,
        envelope.From
    );
    const name =
        pickPartyString(
            source.from_name,
            source.fromName,
            source.sender_name,
            source.senderName
        ) || parsed.name;
    const email =
        pickPartyString(
            source.from_email,
            source.fromEmail,
            source.sender_email,
            source.senderEmail
        ) || parsed.email;

    return {
        name: name || '',
        email: email || '',
        display: name || email || parsed.display || '',
    };
};

export const getMessageFrom = (message) => getMessageFromParty(message).display;

export const getPartyInitials = (name = '', email = '') => {
    const source = (name || email || '').replace(/@.*$/, '');
    const parts = source.split(/[\s._-]+/).filter(Boolean);

    if (parts.length >= 2) {
        return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }

    return source.slice(0, 2).toUpperCase() || '?';
};

export const getMessageTo = (message) => {
    const source = asMessage(message);
    const to =
        source.to_addresses ||
        source.to_name ||
        source.toName ||
        source.to ||
        source.recipients ||
        source.to_email ||
        source.toEmail;

    return formatMailboxAddress(to);
};

export const getMessageCc = (message) => {
    const source = asMessage(message);

    return formatMailboxAddress(
        source.cc ||
        source.cc_email ||
        source.ccEmail ||
        source.cc_addresses
    );
};

export const getMessageBodyHtml = (message) => {
    const source = asMessage(message);

    return source.body_html || source.bodyHtml || source.html || '';
};

export const getMessageBodyText = (message) => {
    const source = asMessage(message);

    return trimMailboxQuotedText(
        source.body_text ||
        source.bodyText ||
        source.text ||
        source.plain ||
        source.body ||
        ''
    );
};

const stripPlainMailboxCss = (value) => String(value || '')
    .replace(/[^{}]*\{[^{}]*:[^{}]*\}/g, ' ');

export const getMessageSnippet = (message, maxLength = 90) => {
    const source = asMessage(message);
    const html = getMessageBodyHtml(source);
    const raw = html
        || source.snippet
        || source.preview
        || source.excerpt
        || getMessageBodyText(source)
        || '';
    const stripped = stripPlainMailboxCss(stripMailboxHtml(raw))
        .replace(/\s+/g, ' ')
        .trim();
    const limit = Number.isFinite(maxLength) && maxLength > 0 ? maxLength : 90;

    if (stripped.length <= limit) {
        return stripped;
    }

    return `${stripped.slice(0, limit).trim()}…`;
};

const formatMailboxAddress = (value) => {
    if (Array.isArray(value)) {
        return value
            .map((item) => formatMailboxAddress(item))
            .filter(Boolean)
            .join(', ');
    }

    return parseMailboxParty(value).display;
};

export const getMessageSubject = (message) => {
    const source = asMessage(message);

    return source.subject || source.title || '';
};

export const stripMailboxSubjectPrefixes = (subject = '') =>
    String(subject || '')
        .replace(/^(?:(?:re|fwd|fw|aw|wg)\s*:\s*)+/i, '')
        .trim();

export const getMessageThread = (message) => {
    const thread = asMessage(message).thread;

    if (!Array.isArray(thread)) {
        return [];
    }

    return thread.filter((item) => item && typeof item === 'object');
};

export const hasMailboxThread = (message) => getMessageThread(message).length > 1;

export const getThreadCount = (message) => {
    const source = asMessage(message);
    const count = Number(source.thread_count ?? source.threadCount);

    if (!Number.isFinite(count) || count <= 1) {
        return 0;
    }

    return Math.floor(count);
};

export const getThreadActionIds = (message) => {
    const source = asMessage(message);
    const raw = source.thread_message_ids ?? source.threadMessageIds;
    const ids = Array.isArray(raw)
        ? raw.filter((id) => id !== null && id !== undefined && id !== '')
        : [];

    if (ids.length) {
        return ids;
    }

    const id = getMessageId(message);

    return id ? [id] : [];
};

export const getMessageFolderName = (message) => {
    const source = asMessage(message);

    return source.folder || source.imap_folder || source.imapFolder || '';
};

const messageTimestamp = (message) => {
    const time = Date.parse(getMessageDate(message) || '');

    return Number.isFinite(time) ? time : null;
};

export const getMailboxConversationSubject = (message) => {
    const thread = getMessageThread(message);
    const items = thread.length ? thread : [asMessage(message)];
    const oldest = items.reduce((current, item) => {
        const currentTime = messageTimestamp(current);
        const itemTime = messageTimestamp(item);

        if (currentTime === null) {
            return item;
        }

        if (itemTime === null) {
            return current;
        }

        return itemTime < currentTime ? item : current;
    });
    const subject = stripMailboxSubjectPrefixes(getMessageSubject(oldest));

    return subject || getMessageSubject(oldest) || '';
};

export const getThreadFolderActionIds = (message) => {
    const thread = getMessageThread(message);
    const folder = String(getMessageFolderName(message) || '').trim().toUpperCase();

    if (!thread.length) {
        return getThreadActionIds(message);
    }

    const matching = folder
        ? thread.filter((item) => (
            String(getMessageFolderName(item) || '').trim().toUpperCase() === folder
        ))
        : thread;
    const ids = (matching.length ? matching : thread)
        .map((item) => getMessageId(item))
        .filter(Boolean);

    return ids.length ? ids : getThreadActionIds(message);
};

export const getUnreadThreadMessageIds = (message) => {
    const thread = getMessageThread(message);
    const items = thread.length ? thread : [asMessage(message)];

    return items
        .filter((item) => isUnreadMessage(item))
        .map((item) => getMessageId(item))
        .filter(Boolean);
};

export const getNewestThreadMessage = (message) => {
    const thread = getMessageThread(message);

    return thread.length ? thread[0] : message;
};

export const getMessageDate = (message) => {
    const source = asMessage(message);

    return source.date ||
        source.received_at ||
        source.receivedAt ||
        source.sent_at ||
        source.sentAt ||
        source.created_at ||
        source.createdAt ||
        source.internal_date ||
        source.internalDate ||
        '';
};

export const formatMailboxDate = (value) => {
    if (!value) {
        return '';
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return String(value);
    }

    return date.toLocaleString();
};

export const formatMailboxListDate = (value) => {
    if (!value) {
        return '';
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return String(value);
    }

    const now = new Date();
    const isToday =
        date.getFullYear() === now.getFullYear() &&
        date.getMonth() === now.getMonth() &&
        date.getDate() === now.getDate();

    if (isToday) {
        return date.toLocaleTimeString(undefined, {
            hour: 'numeric',
            minute: '2-digit',
        });
    }

    if (date.getFullYear() === now.getFullYear()) {
        return date.toLocaleDateString(undefined, {
            month: 'short',
            day: 'numeric',
        });
    }

    return date.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    });
};

const isFalsyFlag = (value) =>
    value === false ||
    value === 0 ||
    value === '0' ||
    value === 'false';

export const isUnreadMessage = (message) => {
    const source = asMessage(message);
    const isRead = source.is_read ?? source.isRead;

    if (isFalsyFlag(isRead) || isFalsyFlag(source.seen)) {
        return true;
    }

    return source.unread === true ||
        source.is_unread === true ||
        source.isUnread === true;
};

export const isStarredMessage = (message) => {
    const source = asMessage(message);

    return source.is_starred === true ||
        source.isStarred === true ||
        source.starred === true ||
        source.flagged === true;
};

export const isTrashFolderMessage = (message, folder = '') => {
    if (folder === 'trash') {
        return true;
    }

    const source = asMessage(message);
    const value = String(
        source.folder ||
        source.imap_folder ||
        source.imapFolder ||
        ''
    ).toUpperCase();

    return value === 'TRASH' || value.includes('TRASH') || value.includes('DELETED');
};

export const buildMailboxFlagPayload = ({
    ids,
    starred,
    unread,
} = {}) => {
    const payload = {};

    if (Array.isArray(ids) && ids.length) {
        payload.ids = ids;
    }

    if (typeof starred === 'boolean') {
        payload.starred = starred;
    }

    if (typeof unread === 'boolean') {
        payload.unread = unread;
    }

    return payload;
};

export const mapMailboxActionError = (
    error,
    t,
    fallback = 'An error occurred while updating the mailbox.'
) => {
    const errorCode = String(error?.response?.data?.error || '').toLowerCase();
    const raw = extractMailboxApiMessage(error);
    const lower = String(raw || errorCode).toLowerCase();

    if (
        errorCode === 'mailbox_message_already_linked' ||
        lower.includes('mailbox_message_already_linked') ||
        lower.includes('already linked')
    ) {
        return t('A case has already been created from this email.');
    }

    if (
        lower.includes('connect microsoft') ||
        lower.includes('oauth tokens missing') ||
        lower.includes('requires oauth') ||
        lower.includes('login is disabled')
    ) {
        return t('Microsoft 365 IMAP requires OAuth. Connect Microsoft in Email settings.');
    }

    if (
        lower.includes('connection reset') ||
        lower.includes('closed the connection')
    ) {
        return t('Mailbox sync was interrupted. Please retry.');
    }

    if (
        lower.includes('invalid intent') ||
        lower.includes('unknown intent') ||
        lower.includes('unsupported intent')
    ) {
        return t('This mailbox intent is not valid.');
    }

    if (error?.response?.status === 403) {
        if (
            lower.includes('active mailer settings are required') ||
            lower.includes('incoming mailer settings not available')
        ) {
            return t('Active mailer settings are required for mailbox');
        }

        if (
            lower.includes('not linked to your reports') ||
            lower.includes('not linked to your cases')
        ) {
            return t('This email is not linked to your reports.');
        }

        if (
            lower.includes('analysts may only reply') ||
            lower.includes('only reply to emails linked')
        ) {
            return t('Analysts may only reply to emails linked to their reports.');
        }
    }

    if (lower.includes('incoming mailer settings not available')) {
        return t('Configure and activate Email settings first.');
    }

    if (lower.includes('active mailer settings are required')) {
        return t('Active mailer settings are required for mailbox');
    }

    if (
        lower.includes('not linked to your reports') ||
        lower.includes('not linked to your cases')
    ) {
        return t('This email is not linked to your reports.');
    }

    if (
        lower.includes('analysts may only reply') ||
        lower.includes('only reply to emails linked')
    ) {
        return t('Analysts may only reply to emails linked to their reports.');
    }

    return t(raw || fallback);
};

export const mapMailboxAnalyzeError = (error, t) =>
    mapMailboxActionError(error, t, 'An error occurred while analyzing this email.');

const unwrapMailboxCollection = (payload, keys = []) => {
    if (Array.isArray(payload)) {
        return payload;
    }

    const root = payload?.data !== undefined && !Array.isArray(payload.data)
        ? payload.data
        : payload;

    if (Array.isArray(root)) {
        return root;
    }

    for (let index = 0; index < keys.length; index += 1) {
        if (Array.isArray(root?.[keys[index]])) {
            return root[keys[index]];
        }
    }

    if (Array.isArray(payload?.data)) {
        return payload.data;
    }

    return [];
};

export const MAILBOX_CASE_THREAD_QUERY_KEY = 'mailbox-case-thread';

export const normalizeCaseThreadResponse = (payload) => {
    const root = payload?.data !== undefined && !Array.isArray(payload.data)
        ? payload.data
        : payload;
    const items = unwrapMailboxCollection(payload, ['items', 'messages']);

    return {
        items,
        requesterEmail:
            root?.requester_email ||
            root?.requesterEmail ||
            payload?.requester_email ||
            '',
    };
};

export const buildCaseReplyPayload = ({
    to = [],
    subject = '',
    body = '',
    parentId = null,
} = {}) => {
    const {
        bodyText,
        bodyHtml,
    } = buildComposeBodies(body, null);
    const payload = {
        to,
        subject: String(subject || '').trim(),
        bodyText,
        bodyHtml,
    };

    if (!parentId) {
        return payload;
    }

    payload.inReplyTo = String(parentId);
    payload.in_reply_to = String(parentId);

    const numericId = Number(parentId);

    if (Number.isFinite(numericId)) {
        payload.inReplyToId = numericId;
        payload.in_reply_to_id = numericId;
    }

    return payload;
};

export const buildMailboxReplySubject = (subject) => {
    const next = String(subject || '').trim();

    if (!next) {
        return 'Re:';
    }

    return /^re:/i.test(next) ? next : `Re: ${next}`;
};

export const getMailboxIntentCode = (value) => {
    if (typeof value === 'string') {
        return value;
    }

    return value?.code || value?.intent || '';
};

export const getMailboxIntentItem = (code) =>
    getMailboxIntentItems().find((item) => item.code === code) || null;

export const getMailboxIntentCount = (value) => {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return Math.max(0, Math.floor(value));
    }

    if (!value || typeof value !== 'object') {
        return 0;
    }

    const count = Number(
        value.count ??
        value.case_count ??
        value.caseCount ??
        value.cases_count ??
        value.casesCount ??
        value.total ??
        0
    );

    if (!Number.isFinite(count) || count <= 0) {
        return 0;
    }

    return Math.floor(count);
};

export const normalizeMailboxIntents = (payload) => {
    const items = unwrapMailboxCollection(payload, ['intents']);
    const countsByCode = {};
    const knownCodes = [];

    items.forEach((item) => {
        const code = getMailboxIntentCode(item);

        if (!getMailboxIntentItem(code)) {
            return;
        }

        knownCodes.push(code);
        countsByCode[code] = getMailboxIntentCount(item);
    });

    const intentItems = getMailboxIntentItems();
    const base = knownCodes.length
        ? intentItems.filter((item) => knownCodes.includes(item.code))
        : intentItems;

    return base.map((item) => ({
        ...item,
        count: countsByCode[item.code] || 0,
    }));
};

export const getMailboxLinkedCaseId = (linkedCase = {}) => {
    const source = linkedCase || {};

    return source.report_case_id ||
        source.reportCaseId ||
        source.case_id ||
        source.caseId ||
        (source.report_case_numeric_id != null
            ? String(source.report_case_numeric_id)
            : null) ||
        (source.reportCaseNumericId != null
            ? String(source.reportCaseNumericId)
            : null);
};

export const normalizeMailboxLinkedCase = (payload, fallbackIntent = '') => {
    if (!payload || typeof payload !== 'object') {
        return null;
    }

    const root = payload.data !== undefined && !Array.isArray(payload.data)
        ? payload.data
        : payload;
    const source = root?.case && typeof root.case === 'object'
        ? root.case
        : root;
    const intent = getMailboxIntentCode(source.intent || source.code || fallbackIntent);
    const reportCaseId = getMailboxLinkedCaseId(source);

    if (!intent || !reportCaseId) {
        return null;
    }

    return {
        intent,
        report_case_id: reportCaseId,
        report_case_numeric_id:
            source.report_case_numeric_id ??
            source.reportCaseNumericId ??
            null,
        report_secondary_case_id: source.secondary_case_id ?? source.secondaryCaseId ?? null,
        secondary_case_id: source.secondary_case_id ?? source.secondaryCaseId ?? null,
        case_reference: source.case_reference ?? null,
        caseReference: source.caseReference ?? null,
    };
};

export const normalizeMailboxLinkedCases = (payload) =>
    unwrapMailboxCollection(payload, ['cases', 'linked_cases', 'linkedCases'])
        .map((item) => normalizeMailboxLinkedCase(item))
        .filter(Boolean);

export const getMessageCases = (message) =>
    normalizeMailboxLinkedCases(asMessage(message).cases);

export const findMailboxLinkedCase = (cases, intent) =>
    (cases || []).find((item) => item.intent === intent) || null;

export const messageHasDsr = (message) => {
    const source = asMessage(message);

    return source.has_dsr === true || source.hasDsr === true;
};

export const messageHasIncident = (message) => {
    const source = asMessage(message);

    return source.has_incident === true || source.hasIncident === true;
};

export const mailboxMessageHasLinkedCase = (message, cases) => {
    const linked = Array.isArray(cases) ? cases : getMessageCases(message);

    return Boolean(
        messageHasDsr(message) ||
        messageHasIncident(message) ||
        linked.length
    );
};

export const getAvailableMailboxIntents = (
    intents,
    message,
    cases
) => {
    const linked = Array.isArray(cases) ? cases : getMessageCases(message);

    if (mailboxMessageHasLinkedCase(message, linked)) {
        return [];
    }

    return (intents || getMailboxIntentItems()).filter(
        (item) => !findMailboxLinkedCase(linked, item.code)
    );
};

export const MAILBOX_ANALYSIS_WARNING_NONE = 'none';
export const MAILBOX_ANALYSIS_WARNING_NOT_RELEVANT = 'not_relevant';
export const MAILBOX_ANALYSIS_WARNING_INSUFFICIENT_DATA = 'insufficient_data';
export const MAILBOX_ANALYSIS_WARNING_LOW_CONFIDENCE = 'low_confidence';

const MAILBOX_ANALYSIS_EXTRACTED_FIELDS = [
    { key: 'title', labelKey: 'Title' },
    { key: 'description', labelKey: 'Description' },
    { key: 'email', labelKey: 'Email' },
    { key: 'first_name', labelKey: 'First name' },
    { key: 'last_name', labelKey: 'Last name' },
    { key: 'request_categories', labelKey: 'Request categories' },
    { key: 'subject_relationship', labelKey: 'Subject relationship' },
    { key: 'incident_date', labelKey: 'Incident date' },
    { key: 'affected_data_types', labelKey: 'Affected data types' },
];

const unwrapMailboxObject = (payload) => {
    if (!payload || typeof payload !== 'object') {
        return null;
    }

    return payload.data !== undefined && !Array.isArray(payload.data)
        ? payload.data
        : payload;
};

export const formatMailboxAnalysisValue = (value) => {
    if (value == null || value === '') {
        return '';
    }

    if (Array.isArray(value)) {
        return value.map(formatMailboxAnalysisValue).filter(Boolean).join(', ');
    }

    if (typeof value === 'object') {
        if (value.title || value.label || value.name) {
            return String(value.title || value.label || value.name);
        }

        if (value.value != null && value.value !== '') {
            return String(value.value);
        }

        return '';
    }

    return String(value);
};

const decodeMailboxHtmlEntities = (value) =>
    String(value || '')
        .replace(/&#x([0-9a-f]+);/gi, (_, hex) => {
            const code = parseInt(hex, 16);

            return Number.isNaN(code) ? '' : String.fromCodePoint(code);
        })
        .replace(/&#(\d+);/g, (_, dec) => {
            const code = parseInt(dec, 10);

            return Number.isNaN(code) ? '' : String.fromCodePoint(code);
        })
        .replace(/&nbsp;/gi, ' ')
        .replace(/&quot;/gi, '"')
        .replace(/&apos;/gi, "'")
        .replace(/&lt;/gi, '<')
        .replace(/&gt;/gi, '>')
        .replace(/&amp;/gi, '&');

export const formatMailboxAnalysisDisplayText = (value) => {
    const raw = formatMailboxAnalysisValue(value);

    if (!raw) {
        return '';
    }

    let text = raw;

    if (typeof document !== 'undefined') {
        const container = document.createElement('div');
        container.innerHTML = DOMPurify.sanitize(raw);
        text = (container.textContent || container.innerText || raw).trim();
    }

    return decodeMailboxHtmlEntities(text)
        .replace(/\u00a0/g, ' ')
        .replace(/\r\n/g, '\n')
        .replace(/[ \t]+/g, ' ')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
};

const MAILBOX_ANALYSIS_CHROME_LINE = /^(jira logo|avatar|view work item|download on the app store|get it on google play|tip:)$/i;
const MAILBOX_ANALYSIS_FOOTER_LINE = /^(this email was sent by|download on the app store|get it on google play|using an ai coding agent)/i;

export const cleanMailboxExtractedPlainText = (value) => {
    let text = formatMailboxAnalysisDisplayText(value);

    if (!text) {
        return '';
    }

    text = text
        .replace(/\[[^\]]*\bhttps?:\/\/[^\]]+\]/gi, '')
        .replace(/https?:\/\/[^\s\]]+/gi, '')
        .replace(/\n\s*\/\s*/g, ' / ');

    const lines = [];
    const sourceLines = text.split('\n');

    for (let index = 0; index < sourceLines.length; index += 1) {
        const trimmed = sourceLines[index].replace(/[ \t]+/g, ' ').trim();

        if (MAILBOX_ANALYSIS_FOOTER_LINE.test(trimmed)) {
            break;
        }

        if (!trimmed) {
            if (lines.length && lines[lines.length - 1] !== '') {
                lines.push('');
            }

            continue;
        }

        if (MAILBOX_ANALYSIS_CHROME_LINE.test(trimmed)) {
            continue;
        }

        lines.push(trimmed);
    }

    return lines.join('\n').replace(/\n{3,}/g, '\n\n').trim();
};

export const normalizeMailboxAnalysis = (payload, intentFallback = '') => {
    const root = unwrapMailboxObject(payload);

    if (!root || typeof root !== 'object') {
        return null;
    }

    const source = root.analysis && typeof root.analysis === 'object' && !Array.isArray(root.analysis)
        ? { ...root, ...root.analysis }
        : root;
    const rawWarningCode = source.warning_code || source.warningCode;
    const warningCode = String(
        rawWarningCode ||
        (source.relevant === false
            ? MAILBOX_ANALYSIS_WARNING_NOT_RELEVANT
            : MAILBOX_ANALYSIS_WARNING_NONE)
    ).toLowerCase() || MAILBOX_ANALYSIS_WARNING_NONE;
    const extracted = source.extracted && typeof source.extracted === 'object'
        ? source.extracted
        : {};
    const questionAnswers = Array.isArray(source.question_answers)
        ? source.question_answers
        : [];
    const resolvedQuestionAnswers = questionAnswers.length
        ? questionAnswers
        : (Array.isArray(source.questionAnswers) ? source.questionAnswers : []);
    const intent = getMailboxIntentCode(source.intent || intentFallback);
    const reason = source.reason || '';
    const warning = source.warning || null;
    const relevant = source.relevant !== false;

    return {
        intent,
        relevant,
        warning,
        warningCode,
        extracted,
        questionAnswers: resolvedQuestionAnswers,
        reason,
        payload: {
            intent,
            relevant,
            warning,
            warning_code: warningCode,
            extracted,
            question_answers: resolvedQuestionAnswers,
            reason,
        },
    };
};

export const serializeMailboxAnalysis = (analysis) => {
    if (!analysis) {
        return null;
    }

    if (analysis.payload && typeof analysis.payload === 'object') {
        return analysis.payload;
    }

    const normalized = analysis.intent || analysis.extracted || analysis.questionAnswers
        ? analysis
        : normalizeMailboxAnalysis(analysis);

    if (!normalized) {
        return null;
    }

    return normalized.payload || null;
};

export const buildMailboxApplyIntentPayload = (intent, analysis) => {
    const payload = {
        intent,
    };
    const serialized = serializeMailboxAnalysis(analysis);

    if (serialized) {
        payload.analysis = serialized;
    }

    return payload;
};

export const getMailboxAnalysisExtractedFields = (analysis) => {
    const extracted = analysis?.extracted || {};

    return MAILBOX_ANALYSIS_EXTRACTED_FIELDS
        .map((field) => {
            const value = field.key === 'description'
                ? cleanMailboxExtractedPlainText(extracted[field.key])
                : formatMailboxAnalysisDisplayText(extracted[field.key]);

            return value ? { ...field, value } : null;
        })
        .filter(Boolean);
};

export const getMailboxAnalysisQuestionAnswers = (analysis) =>
    (analysis?.questionAnswers || []).map((item) => ({
        question: item.question ?? item.question_id ?? item.questionId ?? '',
        answer: formatMailboxAnalysisDisplayText(item.answer),
        reason: formatMailboxAnalysisDisplayText(item.reason),
    }));

export const hasMailboxAnalysisWarning = (analysis) => {
    const code = analysis?.warningCode;

    return Boolean(code) && code !== MAILBOX_ANALYSIS_WARNING_NONE;
};

export const isMailboxAnalysisNotRelevant = (analysis) => (
    analysis?.warningCode === MAILBOX_ANALYSIS_WARNING_NOT_RELEVANT
    || analysis?.relevant === false
);

export const getMailboxAnalysisWarningColor = (warningCode) => {
    if (!warningCode || warningCode === MAILBOX_ANALYSIS_WARNING_NONE) {
        return '';
    }

    if (warningCode === MAILBOX_ANALYSIS_WARNING_LOW_CONFIDENCE) {
        return 'info';
    }

    return 'warning';
};

export const getMailboxAnalysisWarningCopy = (analysis, t, intentLabel) => {
    if (!hasMailboxAnalysisWarning(analysis) && !isMailboxAnalysisNotRelevant(analysis)) {
        return '';
    }

    if (analysis.warning) {
        return analysis.warning;
    }

    switch (analysis.warningCode) {
        case MAILBOX_ANALYSIS_WARNING_NOT_RELEVANT:
            return t(
                'This email does not appear related to {{intent}}. Review the message before creating a case.',
                { intent: intentLabel }
            );
        case MAILBOX_ANALYSIS_WARNING_INSUFFICIENT_DATA:
            return t('This email does not contain enough information to auto-fill a useful case.');
        case MAILBOX_ANALYSIS_WARNING_LOW_CONFIDENCE:
            return t('The AI analysis has low confidence. Review the extracted information before creating a case.');
        default:
            return '';
    }
};

export const getMailboxCaseDetailPath = (linkedCase) => {
    const id = getMailboxLinkedCaseId(linkedCase);

    if (!id) {
        return null;
    }

    return getMailboxIntentItem(linkedCase?.intent)?.caseDetailPath?.(id) || null;
};

export const MAILBOX_BACK_STORAGE_KEY = 'sip.mailboxBackLocation';

export const getMailboxCaseDetailLocation = (linkedCase, fromLocation) => {
    const pathname = getMailboxCaseDetailPath(linkedCase);

    if (!pathname) {
        return null;
    }

    if (!fromLocation?.pathname) {
        return pathname;
    }

    return {
        pathname,
        state: {
            mailboxBackLocation: {
                pathname: fromLocation.pathname,
                search: fromLocation.search || '',
                state: fromLocation.state || null,
            },
        },
    };
};

export const getMailboxToastBackLocation = (fromLocation) => {
    const folder = getMailboxFolderItemByPath(fromLocation?.pathname)
        || getMailboxFolderItemByPath(fromLocation?.state?.mailboxFolderPath);

    return {
        pathname: folder?.path || getMailboxInboxPath(),
        search: folder && fromLocation?.pathname === folder.path
            ? (fromLocation.search || '')
            : '',
        state: null,
    };
};

export const getMailboxBackLocationFromLocation = (location) => {
    if (location?.state?.mailboxBackLocation) {
        return location.state.mailboxBackLocation;
    }

    try {
        const raw = sessionStorage.getItem(MAILBOX_BACK_STORAGE_KEY);

        if (!raw) {
            return null;
        }

        sessionStorage.removeItem(MAILBOX_BACK_STORAGE_KEY);

        return JSON.parse(raw);
    } catch {
        return null;
    }
};

export const storeMailboxBackLocation = (fromLocation) => {
    try {
        sessionStorage.setItem(
            MAILBOX_BACK_STORAGE_KEY,
            JSON.stringify(getMailboxToastBackLocation(fromLocation))
        );
    } catch {
        // Ignore storage failures and fall back to the default Back path.
    }
};

export const getMailboxAppHref = (path) => {
    if (!path) {
        return null;
    }

    const { pathname } = window.location;
    const marker = '/admin/';
    const index = pathname.indexOf(marker);

    if (index > 0) {
        return `${pathname.slice(0, index)}${path}`;
    }

    return path;
};

export const messageHasAttachments = (message) => {
    const source = asMessage(message);

    return source.has_attachments === true || source.hasAttachments === true;
};

export const getMessageAttachments = (message) => {
    const source = asMessage(message);
    const list = source.attachments || source.Files || source.files || [];

    return Array.isArray(list) ? list : [];
};

export const getAttachmentId = (attachment = {}) =>
    attachment.id ?? attachment.attachmentId ?? attachment.attachment_id ?? null;

export const getAttachmentFilename = (attachment = {}) =>
    attachment.filename ||
    attachment.name ||
    attachment.original_name ||
    attachment.originalName ||
    'attachment';

export const getAttachmentContentType = (attachment = {}) =>
    attachment.content_type ||
    attachment.contentType ||
    attachment.mime_type ||
    attachment.mimeType ||
    '';

export const getAttachmentContentId = (attachment = {}) =>
    String(attachment.content_id || attachment.contentId || '')
        .replace(/^<|>$/g, '')
        .trim();

const escapeRegExp = (value) =>
    String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export const htmlReferencesContentId = (html, contentId) => {
    if (!html || !contentId) {
        return false;
    }

    return new RegExp(`cid:(?:<)?${escapeRegExp(contentId)}(?:>)?`, 'i').test(html);
};

export const isInlineAttachment = (attachment = {}) => {
    const value = attachment.is_inline ?? attachment.isInline;

    return value === true || value === 1 || value === '1' || value === 'true';
};

export const isEmbeddedInBodyAttachment = (attachment = {}) => {
    const value = attachment.embedded_in_body ?? attachment.embeddedInBody;

    return value === true || value === 1 || value === '1' || value === 'true';
};

export const splitMessageAttachments = (message) => {
    const inline = [];
    const regular = [];

    getMessageAttachments(message).forEach((attachment) => {
        if (isInlineAttachment(attachment)) {
            inline.push(attachment);
        } else {
            regular.push(attachment);
        }
    });

    return {
        inline,
        regular,
    };
};

export const rewriteCidReferences = (html, cidToUrl = {}) => {
    if (!html) {
        return '';
    }

    let next = html;

    Object.entries(cidToUrl).forEach(([contentId, url]) => {
        if (!contentId || !url) {
            return;
        }

        next = next.replace(
            new RegExp(`cid:(?:<)?${escapeRegExp(contentId)}(?:>)?`, 'gi'),
            url
        );
    });

    return next;
};

const MAILBOX_HTML_URI_REGEXP =
    /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|sms|blob):|data:image\/|\/\/)/i;

export const isSafeMailboxImageSrc = (src = '') =>
    /^(?:https?:|data:image\/|blob:|\/\/)/i.test(String(src).trim());

export const stripUnsafeMailboxImageSources = (html = '') => {
    if (!html) {
        return '';
    }

    const replaceSrc = (_, attr, quote, src) => {
        if (isSafeMailboxImageSrc(src)) {
            return `${attr}${quote}${src}${quote}`;
        }

        return `${attr}${quote}${quote}`;
    };

    return html
        .replace(/(\ssrc\s*=\s*)(["'])([\s\S]*?)\2/gi, replaceSrc)
        .replace(/(\ssrcset\s*=\s*)(["'])([\s\S]*?)\2/gi, replaceSrc)
        .replace(
            /(\ssrc\s*=\s*)([^\s"'=<>`]+)/gi,
            (full, attr, src) => {
                if (isSafeMailboxImageSrc(src)) {
                    return full;
                }

                return `${attr}""`;
            }
        )
        .replace(
            /url\(\s*(['"]?)([\s\S]*?)\1\s*\)/gi,
            (full, quote, src) => (
                isSafeMailboxImageSrc(src) ? full : 'url()'
            )
        );
};

export const sanitizeMailboxHtml = (html = '') => trimQuotedMailboxHtml(
    DOMPurify.sanitize(stripUnsafeMailboxImageSources(html), {
        ALLOWED_URI_REGEXP: MAILBOX_HTML_URI_REGEXP,
    })
);

export const buildOriginalMessagePlainText = (original = {}) => {
    const text = String(original.text || '').trim();
    const hasMeta = original.from || original.date || original.to || original.subject;

    if (!text && !hasMeta) {
        return '';
    }

    return [
        '-----Original Message-----',
        original.from ? `From: ${original.from}` : null,
        original.date ? `Sent: ${original.date}` : null,
        original.to ? `To: ${original.to}` : null,
        original.subject ? `Subject: ${original.subject}` : null,
        '',
        text,
    ].filter((line) => line != null).join('\n');
};

export const buildOriginalMessageHtml = (original = {}) => {
    const inner = original.html
        ? sanitizeMailboxHtml(original.html)
        : escapeMailboxHtml(original.text || '').replace(/\n/g, '<br />');
    const hasMeta = original.from || original.date || original.to || original.subject;

    if (!inner && !hasMeta) {
        return '';
    }

    const meta = [
        original.from ? `<strong>From:</strong> ${escapeMailboxHtml(original.from)}<br />` : '',
        original.date ? `<strong>Sent:</strong> ${escapeMailboxHtml(original.date)}<br />` : '',
        original.to ? `<strong>To:</strong> ${escapeMailboxHtml(original.to)}<br />` : '',
        original.subject ? `<strong>Subject:</strong> ${escapeMailboxHtml(original.subject)}` : '',
    ].join('');

    return (
        `<div class="mailbox-reply-original-content">` +
        `<hr />` +
        (meta ? `<p>${meta}</p>` : '') +
        `<blockquote>${inner}</blockquote>` +
        `</div>`
    );
};

export const isImageContentType = (contentType = '') =>
    String(contentType).toLowerCase().startsWith('image/');

export const parseFilenameFromDisposition = (header, fallback) => {
    if (!header) {
        return fallback;
    }

    const utfMatch = header.match(/filename\*=UTF-8''([^;]+)/i);

    if (utfMatch) {
        try {
            return decodeURIComponent(utfMatch[1]);
        } catch (error) {
            return utfMatch[1];
        }
    }

    const quotedMatch = header.match(/filename="([^"]+)"/i);

    if (quotedMatch) {
        return quotedMatch[1];
    }

    const plainMatch = header.match(/filename=([^;]+)/i);

    if (plainMatch) {
        return plainMatch[1].trim().replace(/^["']|["']$/g, '');
    }

    return fallback;
};

export const saveBlobAsFile = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = filename || 'attachment';
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
};
